import {Coupon_Tier} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {COLORS, FONT_SIZES, Text} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {SvgTiersMapTypes, svgTiersMap} from '../helpers/svgTiersMap';
import {CouponDiscountValue} from './CouponDiscountValue';

interface CouponTiersProps {
  tiers: Coupon_Tier[];
  forceActiveState?: boolean;
  className?: string;
  mode?: SvgTiersMapTypes;
  'data-testid'?: string;
}

export const CouponTiers: React.FC<CouponTiersProps> = ({
  tiers,
  forceActiveState,
  className,
  mode = 'regular',
  'data-testid': dataTestId = 'couponTiers',
}) => {
  return (
    <Wrapper
      data-testid={dataTestId}
      className={className}
      $tiersNumber={tiers.length}
      $mode={mode}
    >
      {tiers.map((tier, index) => (
        <TierContainer
          data-testid="tier"
          key={`couponTier-${index}`}
          data-is-active={tier.type?.$case === 'active' || forceActiveState}
          $mode={mode}
          $tiersNumber={tiers.length}
        >
          {tiers.length > 1 && <TierShape />}
          <TextContainer>
            <StyledCouponDiscountValue>
              {tier.discountValue.value}
              {tier.discountValue.unit}
            </StyledCouponDiscountValue>
            <ConditionText color="inherit">{tier.condition}</ConditionText>
          </TextContainer>
        </TierContainer>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $tiersNumber: number;
  $mode: SvgTiersMapTypes;
}>`
  display: inline-flex;

  ${({$mode}) =>
    $mode === 'compact'
      ? css`
          font-size: ${FONT_SIZES.px46};
          height: 100%;
        `
      : css`
          font-size: ${FONT_SIZES.px28};
          width: 100%;
        `}

  color: ${COLORS.Grey2};
  justify-content: center;

  ${({$tiersNumber, $mode}) =>
    $tiersNumber > 1 && getTierShapes($tiersNumber, $mode)}

  ${({$tiersNumber}) => {
    if ($tiersNumber === 3) {
      return css`
        padding: 1px;
      `;
    } else if ($tiersNumber === 2) {
      return css`
        padding: 2px;
      `;
    }
  }}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  gap: 4px;
`;

const StyledCouponDiscountValue = styled(CouponDiscountValue)`
  padding: 0;
  line-height: 1;
  font-size: 1em;
  color: inherit;
`;

const ConditionText = styled(Text)`
  padding-bottom: 3px;
  margin-inline: 10px;
  font-size: 0.3928571429em;
`;

export const TierShape = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: ${COLORS.Grey2};
  mask-repeat: no-repeat;
  mask-size: 100%;
  mask-position: center;

  transition: background-color 400ms ease-in;
`;

export const TierContainer = styled.div<{
  $mode: SvgTiersMapTypes;
  $tiersNumber: number;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-size: contain;
  background-repeat: no-repeat;
  position: relative;
  transition: color 400ms ease-in;

  ${({$mode}) =>
    $mode === 'regular'
      ? css`
          width: 100%;
        `
      : css`
          height: 100%;
        `}

  ${({$tiersNumber}) => {
    if ($tiersNumber === 3) {
      return css`
        margin: -1px;
      `;
    } else if ($tiersNumber === 2) {
      return css`
        margin: -2px;
      `;
    }
  }}

  &[data-is-active='true'] {
    color: ${COLORS.PrimaryCoupon};

    ${TierShape} {
      background-color: ${COLORS.BorderColorCoupon};
    }
  }
`;

const getTierShapes = (tiersNumber: number, mode: SvgTiersMapTypes) => {
  const {
    threeTierLeft,
    threeTierMiddle,
    threeTierRight,
    twoTierLeft,
    twoTierRight,
  } = svgTiersMap[mode];

  const threeTierAspectRatio = mode === 'regular' ? '1/0.574' : '2/0.87';

  switch (tiersNumber) {
    case 3: {
      return css`
        ${TierContainer} {
          aspect-ratio: ${threeTierAspectRatio};
        }

        ${TierContainer}:nth-child(1) ${TierShape} {
          mask-image: url(${threeTierLeft});
        }
        ${TierContainer}:nth-child(2) ${TierShape} {
          mask-image: url(${threeTierMiddle});
        }
        ${TierContainer}:nth-child(3) ${TierShape} {
          mask-image: url(${threeTierRight});
        }
      `;
    }
    case 2: {
      return css`
        ${TierContainer} {
          aspect-ratio: 2/0.76;
        }

        ${TierContainer}:nth-child(1) ${TierShape} {
          mask-image: url(${twoTierLeft});
        }
        ${TierContainer}:nth-child(2) ${TierShape} {
          mask-image: url(${twoTierRight});
        }
      `;
    }
    case 1: {
      return css`
        display: inline-flex;
      `;
    }
    default:
      return '';
  }
};
